/**
 * DocumentVatRegisterSection.tsx — Dokumenty: RĘCZNY rejestr VAT faktury (read-only).
 *
 * Read-only widok wierszy rejestru VAT — mirror `DocumentItemsSection.tsx` (patrz
 * nagłówek tamtego pliku): tabela + empty state, ZERO fetch/mutate. Wartości kwotowe
 * (`valueNet`/`valueVat`/`valueGross`) i stawka są w PLN (rejestr VAT jest zawsze
 * ewidencją w walucie krajowej, niezależnie od waluty dokumentu źródłowego) — stąd,
 * w odróżnieniu od `DocumentItemsSection`, BRAK parametru/formatowania walutowego —
 * kwoty renderują się jako zwykłe liczby (domyślnie 2 miejsca po przecinku), nie
 * `Intl.NumberFormat style: 'currency'`.
 *
 * Kolumny: stałe (`VAT_REGISTER_SECTION_COLUMN_IDS`) w kolejności WF, a gdy strona poda
 * `columnSettings` (`element.settings` klocka `vat_register_view`) — układ z kreatora formatki:
 * kolejność, widoczność, własna etykieta, wyrównanie, szerokość (`<colgroup>` — tabela przechodzi
 * wtedy na układ stały) i liczba miejsc po przecinku kolumn kwotowych (tylko prezentacja).
 * Ten sam zapis steruje edytorem wierszy (`DocumentVatRegisterEditor`).
 *
 * Kody słownikowe (`registerCode`/`vatRateCode`/`gtuCodes`) rozwiązane do etykiet przez
 * `useDictionary` — mirror edytora (patrz tamten plik), żeby read-only widok po zapisie
 * nie pokazywał surowych kodów (`zakup_krajowy`/`23`/`GTU_01`) niespójnie z resztą strony
 * (UX finding). Lookup po PEŁNEJ liście pozycji (nie tylko `activeItems`) — zapisany kod
 * może wskazywać pozycję, która w międzyczasie przestała być aktywna/widoczna; fallback
 * na surowy kod gdy pozycja nie znaleziona (słownik jeszcze się ładuje / kod osierocony).
 *
 * Edycja (`DocumentVatRegisterEditor`) i wpięcie na stronę faktury/rejestr layoutu są
 * POZA zakresem tego komponentu.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { effectiveColumnAlign } from '@saas/shared';

import { useDictionary } from '@/hooks/useDictionary';
import {
  type DocumentVatRegisterRow,
  vatTaxCategoryLabel,
} from '@/lib/documents/document-vat-register';
import {
  columnAlignClass,
  fixedTableMinWidth,
  formatDecimalCell,
  hasExplicitWidths,
  TableColgroup,
} from '@/lib/embedded-table/cells';
import {
  type VisibleVatRegisterColumn,
  visibleVatRegisterColumns,
} from '@/lib/embedded-table/vat-register-descriptors';

export interface DocumentVatRegisterSectionProps {
  rows: DocumentVatRegisterRow[] | null | undefined;
  /** Kod słownika rejestrów — strona faktury poda właściwy (zakup vs sprzedaż), mirror
   * `DocumentVatRegisterEditorProps.registerDictionaryCode` (ten komponent sam nie wie,
   * na jakiej klasie dokumentu jest osadzony). */
  registerDictionaryCode: 'vat_registers_purchase' | 'vat_registers_sale';
  /** Ustawienia elementu `vat_register_view` z layoutu (`element.settings`): układ kolumn
   * (`tableColumns` + maska `hiddenColumnIds`) — ten sam zapis dla odczytu i edytora wierszy.
   * Brak ustawień → stałe kolumny w kolejności WF, zero zmiany wyglądu względem stanu sprzed
   * konfigurowalności. Kolumny wymagane i zablokowane (Lp., rejestr, stawka, netto, VAT, brutto)
   * są pokazane zawsze. */
  columnSettings?: Record<string, unknown>;
}

const EMPTY_DASH = '—';
const th = 'px-3 py-2 text-xs font-semibold text-text-secondary';
const td = 'px-3 py-2 text-text-primary';

/** Formatuje decimal-string jako zwykłą liczbę — BEZ symbolu waluty (patrz nagłówek pliku:
 * rejestr VAT jest zawsze w PLN, ale bez etykiety waluty w kolumnie, żeby nie sugerować
 * przeliczenia z waluty dokumentu). `decimals` z układu kolumny (tylko prezentacja); brak
 * albo wartość, której nie da się sformatować dokładnie → dotychczasowe 2 miejsca po przecinku. */
function formatAmount(value: string | null | undefined, decimals?: number): string {
  if (value == null || value === '') return EMPTY_DASH;
  const fromLayout = formatDecimalCell(value, decimals);
  if (fromLayout !== null) return fromLayout;
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDate(value: string | null | undefined): string {
  if (value == null || value === '') return EMPTY_DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('pl-PL');
}

export function DocumentVatRegisterSection({
  rows,
  registerDictionaryCode,
  columnSettings,
}: DocumentVatRegisterSectionProps) {
  const { t } = useTranslation();
  const data = rows ?? [];
  // Kolumny widoczne w kolejności układu (bez układu: wszystkie, w kolejności WF).
  const columns = useMemo(() => visibleVatRegisterColumns(t, columnSettings), [t, columnSettings]);
  const columnWidths = columns.map(({ colId, col }) => ({ id: colId, width: col.width }));
  // Jawna szerokość choć jednej kolumny → `table-layout: fixed` + `<colgroup>`; bez niej tabela
  // zostaje w układzie automatycznym, jak dotąd.
  const fixedLayout = hasExplicitWidths(columnWidths);
  const tableMinWidth = fixedTableMinWidth(columnWidths);
  /**
   * Rejestry: te same dwa źródła i to samo pierwszeństwo co w edytorze (słownik
   * zsynchronizowany z Symfonii przed lokalnym) — bez tego kod z FK (u nich to zwykła
   * liczba, np. `5`) nie rozwiązałby się do nazwy i w odczycie widniałaby sama cyfra.
   */
  const fkRegisterDict = useDictionary('fk_vat_registers');
  const localRegisterDict = useDictionary(registerDictionaryCode);
  const registerDict =
    (fkRegisterDict.activeItems ?? []).length > 0 ? fkRegisterDict : localRegisterDict;
  const vatDict = useDictionary('vat_rates');
  const gtuDict = useDictionary('gtu_codes');

  /** Kod → etykieta, fallback na surowy kod (słownik jeszcze ładuje się / kod osierocony). */
  function resolveLabel(itemsByCode: Map<string, { label: string }>, code: string): string {
    return itemsByCode.get(code)?.label ?? code;
  }

  /** Nagłówek: własna etykieta z układu albo etykieta deskryptora. */
  function renderHeaderCell({ col }: VisibleVatRegisterColumn): React.ReactNode {
    return col.label ?? col.descriptor.label;
  }

  function renderBodyCell(
    { colId, col }: VisibleVatRegisterColumn,
    row: DocumentVatRegisterRow,
  ): React.ReactNode {
    // Wyrównanie z układu (jawne, domyślne deskryptora albo wg rodzaju); kwoty z `tabular-nums`.
    const alignClass = columnAlignClass(effectiveColumnAlign(col));
    const tdAligned = `${td} ${alignClass}`;
    const tdAmount = `${td} ${alignClass} tabular-nums`;
    switch (colId) {
      case 'registerCode':
        return (
          <td key={colId} className={tdAligned}>
            {resolveLabel(registerDict.itemsByCode, row.registerCode)}
          </td>
        );
      case 'categoryAbcd':
        return (
          <td key={colId} className={tdAligned}>
            {row.categoryAbcd ?? EMPTY_DASH}
          </td>
        );
      case 'taxCategory':
        return (
          <td key={colId} className={tdAligned}>
            {row.taxCategory ? vatTaxCategoryLabel(row.taxCategory, t) : EMPTY_DASH}
          </td>
        );
      case 'vatRateCode':
        return (
          <td key={colId} className={tdAligned}>
            {resolveLabel(vatDict.itemsByCode, row.vatRateCode)}
          </td>
        );
      case 'valueNet':
        return (
          <td key={colId} className={tdAmount}>
            {formatAmount(row.valueNet, col.decimals)}
          </td>
        );
      case 'valueVat':
        return (
          <td key={colId} className={tdAmount}>
            {formatAmount(row.valueVat, col.decimals)}
          </td>
        );
      case 'valueGross':
        return (
          <td key={colId} className={tdAmount}>
            {formatAmount(row.valueGross, col.decimals)}
          </td>
        );
      case 'valueNetCurrency':
      case 'valueVatCurrency':
      case 'valueGrossCurrency':
        // Kwoty w walucie: `null` = dokument złotówkowy, więc kreska, a nie „0,00" (zero
        // sugerowałoby, że w walucie faktycznie wyszło zero).
        return (
          <td key={colId} className={tdAmount}>
            {formatAmount(row[colId], col.decimals)}
          </td>
        );
      case 'service':
        return (
          <td key={colId} className={tdAligned}>
            {row.service ? t('common.yes') : t('common.no')}
          </td>
        );
      case 'period':
        return (
          <td key={colId} className={tdAligned}>
            {formatDate(row.period)}
          </td>
        );
      case 'lineNumber':
        return (
          <td key={colId} className={`${tdAligned} tabular-nums text-text-tertiary`}>
            {row.lineNumber}
          </td>
        );
      case 'ue':
        return (
          <td key={colId} className={tdAligned}>
            {row.ue ? t('common.yes') : t('common.no')}
          </td>
        );
      case 'gtuCodes':
        return (
          <td key={colId} className={tdAligned}>
            {row.gtuCodes && row.gtuCodes.length > 0
              ? row.gtuCodes.map((code) => resolveLabel(gtuDict.itemsByCode, code)).join(', ')
              : EMPTY_DASH}
          </td>
        );
    }
  }

  return (
    <div data-testid="document-vat-register-section">
      {data.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-border-secondary bg-bg-secondary p-8 text-center"
          data-testid="document-vat-register-empty"
        >
          <p className="text-sm text-text-tertiary">{t('tenant.documents.vatRegister.empty')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-border-primary">
          <table
            className={fixedLayout ? 'w-full table-fixed text-sm' : 'w-full text-sm'}
            style={tableMinWidth !== undefined ? { minWidth: `${tableMinWidth}px` } : undefined}
            data-testid="document-vat-register-table"
          >
            <TableColgroup columns={columnWidths} />
            <thead>
              <tr className="border-b border-border-primary bg-bg-secondary">
                {columns.map((column) => (
                  <th
                    key={column.colId}
                    className={`${th} ${columnAlignClass(effectiveColumnAlign(column.col))}`}
                  >
                    {renderHeaderCell(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-border-primary last:border-0 hover:bg-bg-secondary/30"
                  data-testid={`document-vat-register-row-${row.id}`}
                >
                  {columns.map((column) => renderBodyCell(column, row))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
